import { ICreateReferenceMutation } from 'modules/threads/types';
import { TEST_IDS } from 'mocks/ids';
import { useGetReferenceQuery, useUpdateReferenceMutation } from 'modules/threads/threadsApi';
import { showErrorMessage, showSuccessMessage } from 'modules/alert/utils';
import { useModal } from 'modules/modals/ModalProvider';
import { Spinner } from 'ui';
import { FileForm } from './forms/file-form';
import { LinkForm } from './forms/link-form';
import { loadMessages, selectFilter } from '../../chat/chatSlice';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';

export const EditReferenceModal = ({ referenceId }: { referenceId: string }) => {
  const { data: reference } = useGetReferenceQuery(referenceId);
  const dispatch = useAppDispatch();
  const chatFilter = useAppSelector(selectFilter);
  const { close } = useModal();
  const [editReference, { isLoading: isSubmitting }] = useUpdateReferenceMutation();

  const onSubmit = async (payload: Omit<ICreateReferenceMutation, 'thread'>) => {
    // Hack to get around API weirdness. If referenceType is specified, we need to include a file
    // which we don't want to do if user is just editing name or desc
    if (!payload.file) {
      delete payload.referenceType;
    }
    try {
      await editReference({ ...payload, id: reference!.id, thread: reference!.thread }).unwrap();
      if (reference?.thread && payload.url !== reference.url) {
        await dispatch(loadMessages({ id: reference.thread.toString(), filter: chatFilter }));
      }
      showSuccessMessage('Reference updated successfully');
      close();
    } catch (err) {
      console.error(err);
      showErrorMessage('An error occurred when updating the reference');
    }
  };

  if (!reference) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div data-testid={TEST_IDS.edit_reference_modal} className="h-[calc(100%-24px)] w-full">
      <div className="h-full overflow-auto">
        {reference.referenceType === 'LINK' ? (
          <LinkForm reference={reference} onSubmit={onSubmit} isSubmitting={isSubmitting} />
        ) : (
          <FileForm
            reference={reference}
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
            threadId={String(reference.thread)}
          />
        )}
      </div>
    </div>
  );
};
